import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface PendingTask {
  id: string;
  description: string;
  phase: string;
  hours: number;
  user_id: string;
  user_name: string;
  task_status: string;
  rejection_reason?: string;
}

export interface PendingVoucher {
  id: string;
  description: string;
  amount: number;
  date: string;
  status: string;
}

export interface PMProject {
  id: string;
  name: string;
  project_code: string;
  project_status: string;
  project_phase: string;
  total_amount: number;
  pendingTasks: PendingTask[];
  pendingVouchers: PendingVoucher[];
}

type AssignmentRow = {
  id: string;
  hours: number | null;
  user_id: string | null;
  task_status: string | null;
  rejection_reason: string | null;
  budget_lines: { description: string | null; phase: string | null; project_id: string };
};

type VoucherRow = {
  id: string;
  description: string | null;
  amount: number | null;
  voucher_date: string | null;
  status: string | null;
  project_id: string;
};

/**
 * Fetches active projects where user is production_lead_id,
 * including pending tasks (status = 'completed') and pending vouchers.
 */
export function usePMProjects(userId: string | null) {
  return useQuery({
    queryKey: ['pm-projects', userId],
    queryFn: async (): Promise<PMProject[]> => {
      if (!userId) return [];

      // Fetch projects where user is production lead
      const { data: projects, error: projError } = await supabase
        .from('projects')
        .select('id, name, project_code, project_status, project_phase, total_amount')
        .eq('production_lead_id', userId)
        .in('project_status', ['active', 'kommende'])
        .is('deleted_at', null);
      if (projError) throw projError;
      if (!projects || projects.length === 0) return [];

      const projectIds = projects.map(p => p.id);

      // Fetch all assignments with status 'completed' (pending approval) for these projects
      const { data: assignments, error: assignError } = await supabase
        .from('budget_line_assignments')
        .select('id, hours, user_id, task_status, rejection_reason, budget_lines!inner(description, phase, project_id)')
        .in('budget_lines.project_id', projectIds)
        .eq('task_status', 'completed');
      if (assignError) throw assignError;
      const typedAssignments = (assignments ?? []) as unknown as AssignmentRow[];

      // Fetch user names for assigned users
      const userIds = [...new Set(typedAssignments.map(a => a.user_id))];
      const userMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, full_name')
          .in('id', userIds);
        for (const u of users ?? []) {
          userMap[u.id] = u.full_name ?? 'Ukjent';
        }
      }

      // Fetch pending vouchers for these projects
      const { data: vouchers, error: vouchError } = await supabase
        .from('vouchers')
        .select('id, description, amount, voucher_date, status, project_id')
        .in('project_id', projectIds)
        .eq('status', 'pending_approval');
      if (vouchError) throw vouchError;
      const typedVouchers = (vouchers ?? []) as VoucherRow[];

      // Group by project
      const tasksByProject = new Map<string, PendingTask[]>();
      const vouchersByProject = new Map<string, PendingVoucher[]>();

      for (const a of typedAssignments) {
        const bl = a.budget_lines;
        const pid = bl?.project_id;
        if (!pid) continue;
        const list = tasksByProject.get(pid) ?? [];
        list.push({
          id: a.id,
          description: bl.description ?? '—',
          phase: bl.phase ?? '',
          hours: Number(a.hours ?? 0),
          user_id: a.user_id,
          user_name: userMap[a.user_id] ?? 'Ukjent',
          task_status: a.task_status,
          rejection_reason: a.rejection_reason ?? undefined,
        });
        tasksByProject.set(pid, list);
      }

      for (const v of typedVouchers) {
        const pid = v.project_id;
        if (!pid) continue;
        const list = vouchersByProject.get(pid) ?? [];
        list.push({
          id: v.id,
          description: v.description ?? '—',
          amount: Number(v.amount ?? 0),
          date: v.voucher_date ?? '',
          status: v.status ?? 'pending_approval',
        });
        vouchersByProject.set(pid, list);
      }

      return projects.map(p => ({
        id: p.id,
        name: p.name,
        project_code: p.project_code ?? '',
        project_status: p.project_status ?? '',
        project_phase: p.project_phase ?? '',
        total_amount: Number(p.total_amount ?? 0),
        pendingTasks: tasksByProject.get(p.id) ?? [],
        pendingVouchers: vouchersByProject.get(p.id) ?? [],
      }));
    },
    enabled: !!userId,
    staleTime: 60_000,
  });
}
